import React from "react";
import { motion } from "framer-motion";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FogOverlay from "../components/FogOverlay";
import WindDust from "../components/WindDust"
import RichText from "../i18n/RichText";
import { useLanguage } from "../i18n/LanguageContext";

const Home = () => {
  const { t } = useLanguage();

  return (
    <>
      <div className="relative w-full h-dvh overflow-hidden bg-black flex items-center justify-center bg-home before:absolute before:inset-0 before:content-[''] z-0 before:bg-black/20 2xl:before:bg-transparent">
        <Navbar />
        <FogOverlay />

        <div className="landscapes:pt-10 landscapes:gap-y-3 flex flex-col items-center md:items-start justify-between w-full px-10 gap-y-5 2xl:px-16 2xl:gap-y-10 3xl:px-32 z-30">
          {/* Dekstop View */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="landscapes:text-[35px] hidden md:block text-[#FFEDD6] text-center md:text-start barrio-regular leading-none text-[40px] 2xl:text-[60px] 3xl:text-[75px]"
          >
            <RichText text={t("hero.title")} highlightClass="text-[#FF6355]" />
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="landscapes:text-[12px] text-[#FFEDD6] didot-bold hidden leading-normal md:block 2xl:text-2xl 3xl:text-3xl"
          >
            <RichText text={t("hero.subtitle")} />
          </motion.div>

          <motion.div
            className="hidden md:block"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {/* <button className="transition-all duration-300 hover:text-[#FF6355] hover:bg-[#FFEDD6] tracking-widest mb-5 barrio-regular rounded-xl text-[#FFEDD6] bg-[#FF6355] py-2 px-4 text-xl">{t("hero.ticket")}</button> */}
            <div className="landscapes:text-[12px] text-center md:text-start helvetica-500 tracking-widest text-[#FFEDD6] text-sm 2xl:text-base 3xl:text-lg">
              {t("hero.comingSoon")}
            </div>
            <div className="landscapes:text-[14px] text-center md:text-start didot-bold tracking-normal text-[#FFEDD6] text-lg 2xl:text-xl 3xl:text-2xl">
              {t("hero.venue")}
            </div>
          </motion.div>

          {/* Mobile View */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="absolute block md:hidden text-[#FFEDD6] text-center lg:text-start barrio-regular leading-none text-[40px] top-[16%]"
          >
            <RichText text={t("hero.title")} highlightClass="text-[#FF6355]" />
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-[#FFEDD6] text-center didot-bold block md:hidden leading-snug text-[12px] w-full absolute top-[35%]"
          >
            <RichText text={t("hero.subtitle")} />
          </motion.div>

          <motion.div
            className="block md:hidden absolute bottom-5 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {/* <button className="transition-all duration-300 hover:text-[#FF6355] hover:bg-[#FFEDD6] mb-2 tracking-widest barrio-regular rounded-lg text-[#FFEDD6] bg-[#FF6355] py-1 px-3 text-lg">{t("hero.ticket")}</button> */}
            <div className="text-center helvetica-500 tracking-widest text-[#FFEDD6] text-sm ">
              {t("hero.comingSoon")}
            </div>
            <div className="text-center didot-bold tracking-normal text-[#FFEDD6] text-lg">
              {t("hero.venue")}
            </div>
          </motion.div>
        </div>

        <WindDust />
      </div>

      <div className="w-full bg-[#FFEDD6] p-5 md:p-16 3xl:p-10 overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-center gap-x-20 gap-y-10">
          <div className="flex flex-col items-start justify-center gap-y-3 landscapes:max-w-5xl 2xl:gap-y-8 md:max-w-[15rem] lg:max-w-xl 3xl:max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1 }}
            >
              <div className="helvetica-bold text-[#5E1A20] text-[12px] 2xl:text-[18px] tracking-[0.1em] mb-4">
                {t("about.label")}
              </div>
              <h2 className="barrio-regular text-[#FF6355] leading-none text-[30px] 2xl:text-[50px] 3xl:text-[60px]">
                <RichText
                  text={t("about.title")}
                  highlightClass="text-[#002E3F]"
                />
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="didot-bold text-[#5E1A20] text-sm 2xl:text-xl 3xl:text-2xl text-justify tracking-tight"
            >
              {t("about.paragraph1")}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="helvetica-500 text-[#5E1A20] text-sm 2xl:text-lg 3xl:text-xl text-justify tracking-normal"
            >
              {t("about.paragraph2")}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1 }}
          >
            <img
              className="landscapes:w-full w-[70%] md:w-full 2xl:w-[80%] mx-auto object-contain"
              src="/assets/images/section-2.png"
              alt={t("alt.aboutImage")}
              width="847"
              height="832"
              decoding="async"
            />
          </motion.div>
        </div>
      </div>

      <div className="w-full bg-[#C2D87F] pt-5 md:pt-10 xl:px-20 px-5 2xl:px-0 2xl:pt-10 overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center xl:items-start justify-center 2xl:gap-x-16 3xl:gap-x-20 gap-y-10 relative md:gap-x-8 xl:gap-x-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1 }}
            className="order-last md:order-first 2xl:max-w-2xl 3xl:max-w-full"
          >
            <img
              className="w-[75%] md:w-full 2xl:w-[full] 3xl:w-[80%] mx-auto object-contain"
              src="/assets/images/section-3.png"
              alt={t("alt.identityImage")}
              width="976"
              height="804"
              decoding="async"
            />
          </motion.div>
          <div className="flex flex-col items-start justify-start gap-y-3 2xl:gap-y-8 2xl:max-w-lg 3xl:max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1 }}
            >
              <div className="helvetica-bold text-[#0B2D3D] text-[12px] 2xl:text-[18px] tracking-[0.1em] mb-4">
                {t("identity.label")}
              </div>
              <h2 className="barrio-regular text-[#0B2D3D] leading-none text-[30px] 2xl:text-[50px] 3xl:text-[60px]">
                <RichText
                  text={t("identity.title")}
                  highlightClass="text-[#FF6355]"
                />
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="didot-bold text-[#1F3C27] text-sm 2xl:text-xl 3xl:text-2xl text-justify tracking-tight"
            >
              <RichText text={t("identity.paragraph1")} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="helvetica-500 text-[#1F3C27] text-sm 2xl:text-xl 3xl:text-xl text-justify tracking-normal"
            >
              {t("identity.paragraph2")}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1, delay: 1 }}
              className="2xl:absolute 2xl:right-[5%] 3xl:right-0 2xl:bottom-0 mt-5"
            >
              <img
                className="w-full md:w-[80%] 2xl:w-[45%] 3xl:w-[75%] 2xl:ml-auto 3xl:mx-auto"
                src={t("images.timeline")}
                alt={t("alt.timeline")}
                width="846"
                height="304"
                decoding="async"
              />
            </motion.div>
          </div>
        </div>
      </div>

      <div className="bg-[#522D59] w-full py-7 2xl:py-14 3xl:py-16 -mt-9 2xl:-mt-16 3xl:-mt-20"></div>

      <div className="bg-section-4 w-full p-5 2xl:p-10 overflow-hidden">
        <div className="flex flex-col md:flex-row justify-between gap-10 md:gap-7 lg:gap-10 items-center mx-auto 3xl:max-w-[90%]">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1 }}
            className="flex flex-col gap-y-5 2xl:gap-y-5 3xl:gap-y-10 md:max-w-[15rem] 2xl:max-w-md 3xl:max-w-xl"
          >
            <div className="text-white text-center text-[12px] md:text-base md:text-start helvetica-500 tracking-[0.2em]">
              {t("visit.label")}
            </div>

            <h2 className="helvetica-500 text-center md:text-start text-white tracking-wide text-[25px] md:text-[20px] 2xl:text-[40px] 3xl:text-[60px] leading-tight 2xl:leading-none">
              <RichText text={t("visit.title")} />
            </h2>

            <div className="text-justify helvetica-500 text-white text-[14px] 2xl:text-[18px] 3xl:text-[24px]">
              {t("visit.description")}
            </div>
          </motion.div>

          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-start gap-x-5 md:gap-x-3 3xl:gap-x-8 mb-5 md:mb-3 lg:mb-10"
            >
              <div>
                <img
                  className="w-[80%] 3xl:w-full"
                  src="/assets/images/icon-1.png"
                  alt=""
                  width="51"
                  height="31"
                />
              </div>
              <div className="text-white helvetica-500 text-[14px] 2xl:text-lg 3xl:text-xl">
                <RichText text={t("visit.address")} />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-start gap-x-5 md:gap-x-3 3xl:gap-x-8 mb-5 md:mb-3 lg:mb-10"
            >
              <div>
                <img
                  className="w-[80%] 3xl:w-full object-cover"
                  src="/assets/images/icon-2.png"
                  alt=""
                  width="51"
                  height="63"
                />
              </div>
              <div className="text-white helvetica-500 text-[14px] 2xl:text-lg 3xl:text-xl">
                <RichText text={t("visit.hours")} />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-start gap-x-5 md:gap-x-1 lg:gap-x-3 3xl:gap-x-8"
            >
              <div>
                <img
                  className="w-[80%] 3xl:w-full"
                  src="/assets/images/icon-3.png"
                  alt=""
                  width="51"
                  height="59"
                />
              </div>
              <div className="text-white helvetica-500 text-[14px] 2xl:text-lg 3xl:text-xl">
                <RichText text={t("visit.audience")} />
              </div>
            </motion.div>
          </div>

          {/* Desktop View */}
          <motion.iframe
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="rounded-2xl h-[300px] w-full md:h-[250px] md:w-[200px] lg:h-[320px] lg:w-[300px] 2xl:h-[450px] 2xl:w-[400px]  3xl:h-[606px] 3xl:w-[575px] order-last"
            src="https://www.google.com/maps/embed?pb=!1m10!1m8!1m3!1d668.0245770763792!2d106.79655807109074!3d-6.602498688657454!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sid!4v1780055482026!5m2!1sen!2sid"
            title={t("visit.mapTitle")}
            allowFullScreen={false}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></motion.iframe>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Home;
